/**
 * GIS Aplikasi Peta Pelanggan
 * 
 * Dependencies yang dibutuhkan:
 * npm install leaflet react-leaflet react-leaflet-cluster leaflet.markercluster jszip @tmcw/togeojson
 * 
 * Tambahkan di index.html atau index.css:
 * <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
 * <link rel="stylesheet" href="https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css" />
 * <link rel="stylesheet" href="https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css" />
 */

import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, ZoomControl, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";

// Tambahkan imports CSS untuk leaflet dan markercluster (agar tiles/controls/cluster tampil benar)
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { dataGeoJson } from "../model/Custom";

// ─── Fix default marker icon ───────────────────────────────────────────────
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ─── Custom marker icon ────────────────────────────────────────────────────
const createCustomIcon = (status = "Aktif") => {
  const colors = {
    "Aktif": "#10b981",
    "Cabut Meter": "#ef4444",
    "Segel": "#f59e0b",
  };
  const color = colors[status as keyof typeof colors] || colors["Aktif"];

  return L.divIcon({
    className: "",
    html: `
      <div style="
        position: relative;
        width: 32px;
        height: 40px;
        filter: drop-shadow(0 2px 6px rgba(0,0,0,0.4));
      ">
        <svg viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg" width="32" height="40">
          <path d="M16 0C7.163 0 0 7.163 0 16c0 10.5 16 24 16 24S32 26.5 32 16C32 7.163 24.837 0 16 0z"
            fill="${color}" stroke="white" stroke-width="2"/>
          <circle cx="16" cy="16" r="6" fill="white"/>
        </svg>
      </div>`,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -42],
  });
};

// ─── Sample data (ganti/tambah dari KMZ parser) ────────────────────────────
const SAMPLE_DATA: any[] = dataGeoJson;

// ─── Layer definitions ─────────────────────────────────────────────────────
const MAP_LAYERS = [
  {
    id: "street",
    label: "OpenStreetMap",
    icon: "🗺️",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
  },
  {
    id: "satellite",
    label: "Esri Satellite",
    icon: "🛰️",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "© Esri",
  },
  {
    id: "topo",
    label: "Topography",
    icon: "🏔️",
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: '© <a href="https://opentopomap.org">OpenTopoMap</a>',
  },
];

// ─── KMZ/KML Parser ───────────────────────────────────────────────────────
async function parseKMZ(file: File) {
  try {
    const JSZip = (await import("jszip")).default;
    const toGeoJSON = (await import("@tmcw/togeojson")).kml;

    const zip = await JSZip.loadAsync(file);
    const kmlFile = Object.keys(zip.files).find((f) => f.endsWith(".kml"));
    if (!kmlFile) throw new Error("KML not found in KMZ");

    const kmlText = await zip.files[kmlFile].async("text");
    const parser = new DOMParser();
    const kmlDoc = parser.parseFromString(kmlText, "text/xml");
    const geojson = toGeoJSON(kmlDoc);

    const toStr = (val: any): string => {
      if (!val) return "-";
      if (typeof val === "string") return val;
      // Handle {_type, value} objects from togeojson
      if (typeof val === "object" && "value" in val) return String(val.value);
      if (typeof val === "object") return JSON.stringify(val);
      return String(val);
    };
    console.log(geojson);

    const response = geojson.features
      .filter((f) => f.geometry?.type === "Point")
      .map((f, i) => ({
        id: i + 1,
        name: `Nosambung - ${toStr(f.properties?.nama)}` || `Pelanggan ${i + 1}`,
        address: toStr(f.properties?.kecamatan),
        phone: toStr(f.properties?.phone) || "-",
        status: toStr(f.properties?.status) || "-",
        tarif: toStr(f.properties?.total_jumlah) || "-",
        photo: f.properties?.icon,
        lat: (f.geometry as any).coordinates[1],
        lng: (f.geometry as any).coordinates[0],
      }));
      
    console.log(response);
    return response;
  } catch (e) {
    console.error("KMZ parse error:", e);
    return [];
  }
}

// ─── Layer Switcher Component ──────────────────────────────────────────────
function LayerSwitcher({ activeLayer, onLayerChange }: {
  activeLayer: string;
  onLayerChange: (id: string) => void;
}) {
  const map = useMap();
  return (
    <div style={{
      position: "absolute", top: 16, right: 16, zIndex: 1000,
      display: "flex", gap: 6, flexDirection: "column",
    }}>
      {MAP_LAYERS.map((layer) => (
        <button
          key={layer.id}
          onClick={() => onLayerChange(layer.id)}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 14px", borderRadius: 10,
            border: activeLayer === layer.id ? "2px solid #3b82f6" : "2px solid transparent",
            background: activeLayer === layer.id ? "rgba(59,130,246,0.15)" : "rgba(255,255,255,0.92)",
            backdropFilter: "blur(8px)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            fontSize: 13, fontWeight: 600,
            color: activeLayer === layer.id ? "#1d4ed8" : "#374151",
            transition: "all 0.2s",
            whiteSpace: "nowrap",
          }}
        >
          <span>{layer.icon}</span>
          <span>{layer.label}</span>
        </button>
      ))}
    </div>
  );
}

function LayerSwitcherPanel({ activeLayer, setActiveLayer, isOpen, onToggle } : { activeLayer: string; setActiveLayer: (id: string) => void; isOpen?: boolean; onToggle?: () => void }) {
  const stylesLayer : { [key: string]: React.CSSProperties } = {
      map_control_panel : {
        position: 'absolute',
        bottom: '25px',
        left: '10px',
        zIndex: 1000,
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        width: 'var(--control-panel-width)',
        maxWidth: 'calc(100vw - 40px)',
        maxHeight: 'calc(100vh - 100px)',
        overflow: 'hidden',
        display: (isOpen === false) ? 'none' : 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(0, 0, 0, 0.05)',
    },
    map_control_panel_header : {
      padding: '16px 20px',
      background: 'linear-gradient(135deg, #f8fafc, #ffffff)',
      borderBottom: '1px solid #edf2f7',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
    },
    map_control_body : {
      padding: '16px 20px',
      overflowY: 'auto',
      maxHeight: '70vh'
    },
    form_select : {
      display: 'block',
      width: '100%',
      padding: '0.375rem 2.25rem 0.375rem 0.75rem',
      MozPaddingStart: 'calc(0.75rem - 3px)', // Vendor prefix diawali huruf kapital
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
      color: '#212529',
      backgroundColor: '#fff',
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 0.75rem center',
      backgroundSize: '16px 12px',
      border: '1px solid #ced4da',
      borderRadius: '0.25rem',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      WebkitAppearance: 'none', // Prefix -webkit- jadi Webkit
      MozAppearance: 'none',    // Prefix -moz- jadi Moz
      appearance: 'none',
    },
    legendCard : {
        background: "#f8fafc",
        borderRadius: 12,
        padding: 12,
        marginTop: 16,
        border: '1px solid #edf2f7'
    },
    legendTitle : {
      fontSize: '0.8rem',
      fontWeight: 600,
      color: '#2c3e50',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    legendItem : {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '6px',
      fontSize: '0.8rem',
      color: '#495057',
    },
    container:{
      height: '100vh',
      width: '100%',
      position: 'relative',
      overflow: 'hidden'
    }
  }
  

  return(
    <div style={stylesLayer.map_control_panel} id="controlPanel">
        <div style={stylesLayer.map_control_panel_header} id="controlHeader">
            <div className="d-flex align-items-center gap-2">
                <i className="bi bi-sliders2"></i>
                <h5 className="mb-0 fw-semibold">Layer &amp; Filter</h5>
            </div>
            <button onClick={onToggle} aria-label="Toggle layer panel" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
              <i className="bi bi-chevron-up toggle-icon"></i>
            </button>
        </div>
        
        <div style={stylesLayer.map_control_body} id="controlBody">
            <form method="GET" action="" id="filterForm">
                <input type="hidden" name="search" id="searchHidden" value="">
                </input>
                
                <div className="mb-4">
                    <label className="fw-semibold mb-2">
                        <i className="bi bi-map-fill text-info me-1"></i> BASEMAP
                    </label>
                    <select 
                      style={stylesLayer.form_select} 
                      name="basemap" 
                      id="basemapSelect"
                      value={activeLayer}
                      onChange={(e) => setActiveLayer(e.target.value)}
                    >
                      {MAP_LAYERS.map((layer) => (
                      <option key={layer.id} value={layer.id}>
                        {layer.icon} {layer.label}
                      </option>
                      ))}
                    </select>
                </div>
            </form>
        </div>
    </div>
  );
}

// ─── Popup Card Component ──────────────────────────────────────────────────
function CustomerPopup({ customer } :  { customer: any }) {
  const statusStyle = {
    "Aktif": { bg: "#d1fae5", color: "#065f46", label: "Aktif" },
    "Cabut Meter": { bg: "#fee2e2", color: "#991b1b", label: "Cabut Meter" },
    "Segel": { bg: "#fef3c7", color: "#92400e", label: "Segel" },
  };
  type StatusType = 'Aktif' | 'Cabut Meter' | 'Segel';

  const s = statusStyle[customer.status as StatusType] || statusStyle["Aktif"];

  return (
    <div style={{
      fontFamily: "'DM Sans', sans-serif",
      width: 260, borderRadius: 14, overflow: "hidden",
      boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    }}>
      {/* Photo */}
      <div style={{ position: "relative", height: 140, background: "#e5e7eb" }}>
        {customer.photo ? (
          <img
            src={customer.photo}
            alt={customer.name}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={{
            width: "100%", height: "100%",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 40, color: "#9ca3af",
          }}>🏠</div>
        )}
        <div style={{
          position: "absolute", top: 10, right: 10,
          background: s.bg, color: s.color,
          padding: "3px 10px", borderRadius: 20,
          fontSize: 11, fontWeight: 700,
        }}>{s.label}</div>
      </div>

      {/* Info */}
      <div style={{ padding: "14px 16px", background: "white" }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
          {customer.name} - {customer.nomorSambung}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, lineHeight: 1.5 }}>
          📍 {customer.address}
        </div>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr",
          gap: 8, paddingTop: 10,
          borderTop: "1px solid #f3f4f6",
        }}>
          <InfoChip icon="📞" label="Telepon" value={customer.phone} />
          <InfoChip icon="⚡" label="Tarif" value={customer.tarif} />
        </div>
      </div>
      <div>
        <div style={{ padding: "14px 16px", background: "#f8fafc", borderTop: "1px solid #e5e7eb" }}>
          <button
            onClick={() => {
              const mapsUrl = `https://www.google.com/maps/search/${customer.lat},${customer.lng}`;
              window.open(mapsUrl, '_blank');
            }}
            style={{
              width: "100%",
              padding: "10px 16px",
              background: "#4285f4",
              color: "white",
              border: "none",
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#3367d6")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#4285f4")}
          >
            🗺️ Buka di Google Maps
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoChip({ icon, label, value }: { 
  icon: React.ReactNode; 
  label: string; 
  value: string 
}) {
  return (
    <div style={{
      background: "#f9fafb", borderRadius: 8,
      padding: "8px 10px",
    }}>
      <div style={{ fontSize: 10, color: "#9ca3af", marginBottom: 2 }}>{icon} {label}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: "#1f2937" }}>{value}</div>
    </div>
  );
}

// ─── Stats Bar ─────────────────────────────────────────────────────────────
function StatsBar({ data }: {data: any[]}) {
  const total = data.length;
  const aktif = data.filter((d) => d.status === "Aktif").length;
  const nonaktif = data.filter((d) => d.status === "Cabut Meter").length;
  const pending = data.filter((d) => d.status === "Segel").length;

  return (
    <div style={{
      display: "flex", gap: 10, flexWrap: "wrap",
      padding: "12px 16px",
      background: "white",
      borderBottom: "1px solid #e5e7eb",
    }}>
      {[
        { label: "Total", value: total, color: "#3b82f6" },
        { label: "Aktif", value: aktif, color: "#10b981" },
        { label: "Cabut Meter", value: nonaktif, color: "#ef4444" },
        { label: "Segel", value: pending, color: "#f59e0b" },
      ].map((s) => (
        <div key={s.label} style={{
          flex: "1 1 60px",
          textAlign: "center",
          background: s.color + "12",
          border: `1px solid ${s.color}30`,
          borderRadius: 10, padding: "8px 6px",
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// ─── Main App ──────────────────────────────────────────────────────────────
export default function GISApp() {
  const [customers, setCustomers] = useState(SAMPLE_DATA);
  const [filtered, setFiltered] = useState(SAMPLE_DATA);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("semua");
  const [activeLayer, setActiveLayer] = useState("street");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  // new: control panel open & mobile detection
  const [controlPanelOpen, setControlPanelOpen] = useState<boolean>(true);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobileView(mobile);
      // default: sembunyikan panel di mobile, tampil di desktop
      setControlPanelOpen(!mobile);
      // small timeout to invalidate map size after layout change
      setTimeout(() => mapRef.current?.invalidateSize(), 250);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // panggil invalidateSize ketika layout berubah (sidebar toggle, pergantian layer)
  useEffect(() => {
    if (!mapRef.current) return;
    // beri sedikit delay agar layout selesai lalu invalidate
    const t = setTimeout(() => mapRef.current?.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, [sidebarOpen, activeLayer]);

  // Filter logic
  useEffect(() => {
    let result = customers.map((f, i) => {
      return {
        id: i + 1,
        // Menggunakan nosamw atau nama
        name: f.nama || "-",
        nomorSambung : f.nosamw || "-",
        // Mengambil data kecamatan/desa
        address: f.kecamatan || f.desa || "-",
        // Menyesuaikan key nomorhp
        phone: f.nomorhp || "-",
        status: f.status || "-",
        // Mengambil total_jumlah
        tarif: f.total_jumlah || "-",
        // Photo (jika ada properti icon, jika tidak bisa pakai placeholder)
        photo: f.icon || null, 
        // Mengambil lat dan lng langsung dari root object
        lat: f.lat,
        lng: f.lng,
        // Kamu bisa simpan warna juga jika perlu
        color: f.color 
      };
    });
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.address.toLowerCase().includes(q) ||
          c.phone.includes(q)
      );
    }
    if (statusFilter !== "semua") {
      result = result.filter((c) => c.status === statusFilter);
    }
    setFiltered(result);
  }, [search, statusFilter, customers]);

  // KMZ import
  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    setImporting(true);
    try {
      const parsed = await parseKMZ(file);
      if (parsed.length > 0) {
        setCustomers((prev : any) => [...prev, ...parsed]);
        alert(`✅ Berhasil import ${parsed.length} titik dari KMZ!`);
      } else {
        alert("⚠️ Tidak ada titik koordinat ditemukan di file KMZ.");
      }
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };

  const currentLayer = MAP_LAYERS.find((l) => l.id === activeLayer);

  return (
    <>
      {/* Google Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'DM Sans', sans-serif; }
        .leaflet-popup-content-wrapper {
          padding: 0 !important;
          border-radius: 14px !important;
          overflow: hidden !important;
          box-shadow: 0 8px 32px rgba(0,0,0,0.2) !important;
        }
        .leaflet-popup-content { margin: 0 !important; }
        .leaflet-popup-tip-container { display: none; }
        .marker-cluster-small, .marker-cluster-medium, .marker-cluster-large {
          background-color: rgba(59,130,246,0.2) !important;
        }
        .marker-cluster-small div, .marker-cluster-medium div, .marker-cluster-large div {
          background-color: rgba(59,130,246,0.9) !important;
          color: white !important;
          font-weight: 700 !important;
          font-family: 'DM Sans', sans-serif !important;
        }
      `}</style>

      <div style={{
        display: "flex", flexDirection: "column",
        height: "100vh", // ganti dari 100dvh ke 100vh untuk kompatibilitas
        width: "100%",
        fontFamily: "'DM Sans', sans-serif",
        overflow: "hidden",
      }}>

        {/* ── Header ── */}
        <div style={{
          background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
          color: "white", padding: "12px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          boxShadow: "0 2px 12px rgba(30,64,175,0.4)",
          zIndex: 100,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: "rgba(255,255,255,0.15)", border: "none",
                borderRadius: 8, width: 36, height: 36,
                cursor: "pointer", color: "white", fontSize: 18,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >☰</button>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.2 }}>GIS Peta Pelanggan</div>
              <div style={{ fontSize: 11, opacity: 0.75 }}>{filtered.length} titik ditampilkan</div>
            </div>
          </div>

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.35)",
              borderRadius: 8, color: "white",
              padding: "7px 14px", cursor: "pointer",
              fontSize: 12, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {importing ? "⏳ Loading..." : "📂 Import KMZ"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".kmz,.kml"
            style={{ display: "none" }}
            onChange={handleFileImport}
          />
        </div>

        {/* ── Stats ── */}
        <StatsBar data={customers} />

        {/* ── Main Content ── */}
        <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>

          {/* ── Sidebar ── */}
          <div style={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            width: sidebarOpen ? 300 : 0,
            background: "white",
            boxShadow: sidebarOpen ? "4px 0 20px rgba(0,0,0,0.15)" : "none",
            zIndex: 500, overflow: "hidden",
            transition: "width 0.3s ease",
            display: "flex", flexDirection: "column",
          }}>
            <div style={{
              padding: 16, borderBottom: "1px solid #e5e7eb",
              background: "#f8fafc",
            }}>
              <input
                placeholder="🔍  Cari nama, alamat..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%", padding: "10px 14px",
                  border: "1.5px solid #e5e7eb", borderRadius: 10,
                  fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                  outline: "none", background: "white",
                }}
              />
              <div style={{
                display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap",
              }}>
                {["semua", "Aktif", "Cabut Meter", "Segel"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    style={{
                      padding: "5px 12px", borderRadius: 20, border: "none",
                      cursor: "pointer", fontSize: 12, fontWeight: 600,
                      background: statusFilter === s ? "#3b82f6" : "#f1f5f9",
                      color: statusFilter === s ? "white" : "#475569",
                      textTransform: "capitalize",
                    }}
                  >{s}</button>
                ))}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              {filtered.map((c) => (
                <div
                  key={c.id}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "1px solid #f1f5f9",
                    cursor: "pointer",
                    display: "flex", gap: 12, alignItems: "center",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 10,
                    overflow: "hidden", flexShrink: 0,
                    background: "#e5e7eb",
                  }}>
                    {c.photo
                      ? <img src={c.photo} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🏠</div>
                    }
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 2 }}>{c.name}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 2 }}>NOSAMB : {c.nomorSambung}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.address}</div>
                    <div style={{
                      marginTop: 4, display: "inline-block",
                      padding: "2px 8px", borderRadius: 20,
                      fontSize: 10, fontWeight: 700,
                      background: c.status === "Aktif" ? "#d1fae5" : c.status === "Cabut Meter" ? "#fee2e2" : "#fef3c7",
                      color: c.status === "Aktif" ? "#065f46" : c.status === "Cabut Meter" ? "#991b1b" : "#92400e",
                    }}>{c.status}</div>
                  </div>
                  <button
                    title="Lihat di peta"
                    onClick={() => {
                      if (mapRef.current) {
                        mapRef.current.flyTo([c.lat, c.lng], 17, { animate: true, duration: 1.2 });
                        setSidebarOpen(false);
                      }
                    }}
                    style={{
                      flexShrink: 0,
                      background: "#eff6ff",
                      border: "1.5px solid #bfdbfe",
                      borderRadius: 8,
                      width: 34, height: 34,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer", fontSize: 16,
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#dbeafe")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "#eff6ff")}
                  >📷</button>
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                  <div style={{ fontSize: 40 }}>🔍</div>
                  <div style={{ marginTop: 8, fontWeight: 600 }}>Tidak ada data</div>
                </div>
              )}
            </div>
          </div>

          {/* Overlay to close sidebar on mobile */}
          {sidebarOpen && (
            <div
              onClick={() => setSidebarOpen(false)}
              style={{
                position: "absolute", inset: 0,
                background: "rgba(0,0,0,0.3)",
                zIndex: 499,
              }}
            />
          )}

          {/* ── Map ── */}
          <div style={{ flex: 1, position: "relative" }}>
            <MapContainer
              ref={mapRef}
              center={[0.467592, 121.931831]}
              zoom={13}
              style={{ width: "100%", height: "100%" }}
              zoomControl={false}
            >
              <ZoomControl position="bottomright" />

              <TileLayer
                key={activeLayer}
                url={currentLayer?.url ?? ""}
                attribution={currentLayer?.attribution}
                maxZoom={20}
              />

              <LayerSwitcherPanel activeLayer={activeLayer} setActiveLayer={setActiveLayer} isOpen={controlPanelOpen} onToggle={() => setControlPanelOpen(prev => !prev)} />

              <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={60}
                showCoverageOnHover={false}
              >
                {filtered.map((customer) => (
                  <Marker
                    key={customer.id}
                    position={[customer.lat, customer.lng]}
                    icon={createCustomIcon(customer.status)}
                  >
                    <Popup minWidth={260} maxWidth={280}>
                      <CustomerPopup customer={customer} />
                    </Popup>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            </MapContainer>

            {/* Floating controls: tombol untuk munculkan panel di mobile */}
            <div style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1200,
              display: 'flex',
              gap: 8,
              flexDirection: 'column',
            }}>
              {/* tombol toggle panel (tampak di mobile) */}
              <button
                onClick={() => setControlPanelOpen(prev => !prev)}
                title={controlPanelOpen ? "Tutup panel" : "Buka panel"}
                style={{
                  background: '#ffffffdd',
                  border: '1px solid rgba(0,0,0,0.08)',
                  padding: '8px 10px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                }}
              >
                ☰
              </button>

              {/* Zoom controls */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}>
                <button
                  onClick={() => mapRef.current?.zoomIn()}
                  title="Zoom in"
                  style={{
                    background: '#fff',
                    border: '1px solid rgba(0,0,0,0.08)',
                    padding: '8px 10px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                  }}
                >＋</button>
                <button
                  onClick={() => mapRef.current?.zoomOut()}
                  title="Zoom out"
                  style={{
                    background: '#fff',
                    border: '1px solid rgba(0,0,0,0.08)',
                    padding: '8px 10px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
                  }}
                >－</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}